import { ExternalLink } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { adminHref } from '../../../../config/config'
import { AgentRunStatus, CoiGeneratorAgentRun } from '../../../../models/coiGenerator'

interface AgentRunCardProps {
  agentRun: CoiGeneratorAgentRun | null
}

function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null) return '—'
  return latencyMs < 1000 ? `${latencyMs} ms` : `${(latencyMs / 1000).toFixed(1)} s`
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground mb-1 text-sm font-medium">{label}</div>
      {children}
    </div>
  )
}

/** Surfaces the LLM parse behind a certificate so a bad COI can be traced to its run. */
export function AgentRunCard({ agentRun }: AgentRunCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Run</CardTitle>
      </CardHeader>
      <CardContent>
        {agentRun ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Row label="Model">
                <span>
                  {agentRun.agentModel} <span className="text-muted-foreground">({agentRun.vendor})</span>
                </span>
              </Row>
              <Row label="Status">
                <span className={agentRun.status === AgentRunStatus.Error ? 'text-red-600' : undefined}>
                  {agentRun.status}
                </span>
              </Row>
              <Row label="Latency">
                <span>{formatLatency(agentRun.latencyMs)}</span>
              </Row>
              <Row label="Debug">
                <a
                  href={adminHref(agentRun.adminUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  View full run in admin
                  <ExternalLink className="size-3" />
                </a>
              </Row>
            </div>
            {agentRun.error && (
              <Row label="Error">
                <span className="font-mono text-sm break-all text-red-600">{agentRun.error}</span>
              </Row>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">No agent run recorded</span>
        )}
      </CardContent>
    </Card>
  )
}
